import { RegisterOptions } from "react-hook-form";
import {
  match,
  useParams as useParamsOriginal,
  useRouteMatch as useRouteMatchOriginal,
} from "react-router-dom";

export type ExtractRouteOptionalParam<
  T extends string
> = T extends `${infer Param}?`
  ? { [k in Param]?: string }
  : { [k in T]: string };

export type ExtractRouteParams<
  T extends string
> = T extends `${string}:${infer Param}/${infer Rest}`
  ? ExtractRouteOptionalParam<Param> & ExtractRouteParams<Rest>
  : T extends `${string}:${infer Param}`
  ? ExtractRouteOptionalParam<Param>
  : {};

/**
 * typed alias for useParams
 */
export const useParams = useParamsOriginal as <T extends string>(
  path: T
) => ExtractRouteParams<T>;

/**
 * typed alias for useRouteMatch
 */
export const useRouteMatch = useRouteMatchOriginal as <T extends string>(
  path: T
) => match<ExtractRouteParams<T>> | null;

type APIPropertySchema = {
  type: string;
  format?: string;
  isReadOnly?: boolean;
  isNullable?: boolean;
  isRequired?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
};

/**
 * convert from the generated OpenAPI schema to a validation definition of `react-hook-form`
 */
export function validationFromSchema(
  schema: APIPropertySchema
): RegisterOptions {
  return {
    required: schema.isRequired ? "must be set" : undefined,
    minLength: schema.minLength
      ? {
          value: schema.minLength,
          message: `must be at least ${schema.minLength} characters long`,
        }
      : undefined,
    maxLength: schema.maxLength
      ? {
          value: schema.maxLength,
          message: `must not be more than ${schema.maxLength} characters long`,
        }
      : undefined,
    pattern: schema.pattern
      ? {
          value: new RegExp(schema.pattern),
          message: "does not have the right format",
        }
      : undefined,
    valueAsNumber: schema.type === "number",
    valueAsDate: schema.type === "string" && schema.format === "date-time",
  };
}
